/**
 * AchievementGrid — displays all achievements with earned/locked states.
 *
 * Earned badges show with full colour and animation.
 * Locked badges show greyed out with criteria description.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { formatDateZA } from '../../utils/dateHelpers';

export default function AchievementGrid({ earned = [], allAchievements = [] }) {
  const earnedIds = new Set(earned.map((e) => e.id));

  return (
    <div className="achievement-grid" style={styles.grid}>
      {allAchievements.map((achievement, idx) => {
        const isEarned = earnedIds.has(achievement.id);
        const earnedData = earned.find((e) => e.id === achievement.id);

        return (
          <motion.div
            key={achievement.id}
            style={{
              ...styles.card,
              ...(isEarned ? styles.cardEarned : styles.cardLocked),
            }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04, duration: 0.3 }}
            whileHover={{ scale: isEarned ? 1.03 : 1 }}
          >
            <div style={{
              ...styles.emojiWrap,
              background: isEarned
                ? 'linear-gradient(135deg, rgba(244, 162, 97, 0.15), rgba(46, 196, 182, 0.12))'
                : '#F0F4F8',
            }}>
              {isEarned ? (
                <motion.span
                  style={styles.emoji}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: idx * 0.04 + 0.1 }}
                >
                  {achievement.emoji}
                </motion.span>
              ) : (
                <Lock size={20} color="#C0C8D4" />
              )}
            </div>
            <div style={styles.info}>
              <div style={{
                ...styles.name,
                color: isEarned ? 'var(--cp-text)' : '#A0AABB',
              }}>
                {achievement.name}
              </div>
              <div style={{
                ...styles.description,
                color: isEarned ? '#6B7A8D' : '#B8C0CC',
              }}>
                {achievement.description || achievement.check?.toString()}
              </div>
              {isEarned && earnedData?.earnedAt && (
                <div style={styles.earnedDate}>
                  Earned {formatDateZA(earnedData.earnedAt)}
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '0.65rem',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.7rem',
    padding: '0.75rem',
    borderRadius: '12px',
    transition: 'all 0.2s',
  },
  cardEarned: {
    background: 'white',
    boxShadow: '0 2px 12px rgba(15, 76, 117, 0.08)',
    border: '1px solid rgba(46, 196, 182, 0.2)',
  },
  cardLocked: {
    background: '#FAFBFC',
    border: '1px solid #E8EFF5',
    opacity: 0.75,
  },
  emojiWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    flexShrink: 0,
  },
  emoji: {
    fontSize: '1.5rem',
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: '0.1rem',
  },
  description: {
    fontSize: '0.72rem',
    lineHeight: 1.3,
  },
  earnedDate: {
    fontSize: '0.65rem',
    color: 'var(--cp-success)',
    fontWeight: 500,
    marginTop: '0.15rem',
  },
};
